import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Check, 
  Edit2, 
  Trash2, 
  X, 
  Save,
  Clock,
  CheckCircle,
  Circle
} from 'lucide-react';
import { 
  saveDailyTodo, 
  updateDailyTodo, 
  deleteDailyTodo, 
  getDailyTodos 
} from '../firebase/subcollections';
import { shouldResetTodos } from '../utils/cleanupExpiredTodos';
import toast from 'react-hot-toast';

const DailyTodo = ({ userId }) => {
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastCheckedDate, setLastCheckedDate] = useState(null);

  // Function to get current IST date
  const getCurrentISTDate = () => {
    const now = new Date();
    const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    return istTime.toDateString();
  };

  // Load todos on component mount
  useEffect(() => {
    if (userId) {
      loadTodos();
      setLastCheckedDate(getCurrentISTDate());
    }
  }, [userId]);

  // Check for date change and reset todos if needed
  useEffect(() => {
    if (!userId) return;

    const checkDateChange = () => {
      const currentDate = getCurrentISTDate();
      
      // If date has changed or it's reset time, reload todos
      if (lastCheckedDate !== currentDate || shouldResetTodos()) {
        loadTodos();
        setLastCheckedDate(currentDate);
      }
    };

    // Check every minute for date changes
    const intervalId = setInterval(checkDateChange, 60 * 1000);
    
    // Also check immediately
    checkDateChange();

    return () => clearInterval(intervalId);
  }, [userId, lastCheckedDate]);

  const loadTodos = async () => {
    try {
      setLoading(true);
      const result = await getDailyTodos(userId);
      if (result.success) {
        // Filter out expired todos on client side as well
        const currentTime = new Date();
        const validTodos = result.data.filter(todo => {
          if (todo.expiresAt) {
            return new Date(todo.expiresAt.toDate ? todo.expiresAt.toDate() : todo.expiresAt) > currentTime;
          }
          return true;
        });
        setTodos(validTodos);
      }
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  const handleAddTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim() || saving) return;

    // Check if userId is available
    if (!userId) {
      toast.error('Please log in to add tasks');
      return;
    }

    try {
      setSaving(true);
      const result = await saveDailyTodo(userId, {
        text: newTodo.trim(),
        priority: 'medium'
      });

      if (result.success) {
        setNewTodo('');
        await loadTodos(); // Reload to get the new todo
        toast.success('Task added successfully!');
      } else {
        // Show error message if save failed
        const errorMessage = result.error?.message || 'Failed to add task. Please try again.';
        toast.error(errorMessage);
        console.error('Error adding todo:', result.error);
      }
    } catch (error) {
      // Handle error with user feedback
      const errorMessage = error?.message || 'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);
      console.error('Error in handleAddTodo:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (todoId, currentStatus) => {
    // handleToggleComplete called
    try {
      const result = await updateDailyTodo(userId, todoId, {
        completed: !currentStatus
      });

      // updateDailyTodo result

      if (result.success) {
        setTodos(prev => {
          const updated = prev.map(todo => 
            todo.id === todoId 
              ? { ...todo, completed: !currentStatus }
              : todo
          );
          // Updated todos
          return updated;
        });
      } else {
        // Failed to update todo
      }
    } catch (error) {
      // Error in handleToggleComplete
    }
  };

  const handleStartEdit = (todo) => {
    setEditingId(todo.id);
    setEditingText(todo.text);
  };

  const handleSaveEdit = async (todoId) => {
    if (!editingText.trim()) return;

    try {
      const result = await updateDailyTodo(userId, todoId, {
        text: editingText.trim()
      });

      if (result.success) {
        setTodos(prev => prev.map(todo => 
          todo.id === todoId 
            ? { ...todo, text: editingText.trim() }
            : todo
        ));
        setEditingId(null);
        setEditingText('');
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      const result = await deleteDailyTodo(userId, todoId);
      if (result.success) {
        setTodos(prev => prev.filter(todo => todo.id !== todoId));
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const completedCount = todos.filter(todo => todo.completed).length;
  const totalCount = todos.length;

  return (
    <div className="h-full flex flex-col p-6 sm:p-8">
      {/* Enhanced Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Today's Tasks</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Stay organized and productive</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
          <Clock className="w-4 h-4" />
          <span>Resets at 12:00 AM IST</span>
        </div>
      </div>

      {/* Enhanced Completion Status */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-base font-medium text-gray-700 dark:text-gray-300">
            {completedCount} of {todos.length} completed
          </span>
          {todos.length > 0 && (
            <span className="text-base font-bold text-blue-600 dark:text-blue-400">
              {Math.round((completedCount / todos.length) * 100)}%
            </span>
          )}
        </div>
        {todos.length > 0 && (
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${(completedCount / todos.length) * 100}%` }}
            ></div>
          </div>
        )}
      </div>

      {/* Enhanced Add Task Form */}
      <form onSubmit={handleAddTodo} className="mb-8">
        <div className="flex space-x-3">
          <input
            type="text"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            placeholder="Add a new task..."
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white text-sm transition-all duration-200"
            disabled={saving}
          />
          <button
            type="submit"
            disabled={!newTodo.trim() || saving}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            {saving ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Adding...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add</span>
              </div>
            )}
          </button>
        </div>
      </form>

      {/* Enhanced Todo List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-14 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
              </div>
            ))}
          </div>
        ) : todos.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Circle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">No tasks for today</h3>
              <p className="text-base text-gray-600 dark:text-gray-400">Add your first task above to get started</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {todos.map((todo) => (
                <motion.div
                  key={todo.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                  className={`group relative bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200 ${
                    todo.completed ? 'opacity-75' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        // Checkbox clicked for todo
                        handleToggleComplete(todo.id, todo.completed);
                      }}
                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 transition-all duration-200 ${
                        todo.completed
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 dark:border-gray-600 hover:border-green-500 dark:hover:border-green-500'
                      }`}
                    >
                      {todo.completed && <Check className="w-4 h-4" />}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      {editingId === todo.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(todo.id)}
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveEdit(todo.id)}
                            className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-base font-medium transition-all duration-200 ${
                              todo.completed
                                ? 'text-gray-500 dark:text-gray-400 line-through'
                                : 'text-gray-900 dark:text-white'
                            }`}
                          >
                            {todo.text}
                          </span>
                          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <button
                              onClick={() => {
                                setEditingId(todo.id);
                                setEditingText(todo.text);
                              }}
                              className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTodo(todo.id)}
                              className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyTodo;

